import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bell, BellOff, X, Calendar, Phone } from 'lucide-react';
import { api } from '@lib/api';
import { cn } from '@lib/utils';

interface HatirlatmaMusteri {
  id: string;
  ad: string;
  soyad: string | null;
  unvan: string | null;
  tipi: 'BIREYSEL' | 'KURUMSAL';
}

interface AktiviteHatirlatma {
  id: string;
  baslik: string;
  tip: string;
  tarih: string;
  hatirlatma: string;
  musteriId: string;
  firsatId: string | null;
  musteri: HatirlatmaMusteri | null;
}

interface OlayHatirlatma {
  id: string;
  baslik: string;
  tip: string;
  baslangic: string;
  hatirlatmaDk: number | null;
  hatirlatmaZamani: string;
  musteriId: string | null;
  musteri: HatirlatmaMusteri | null;
}

interface HatirlatmaResp {
  simdi: string;
  ileriDk: number;
  aktiviteler: AktiviteHatirlatma[];
  olaylar: OlayHatirlatma[];
}

const NOTIF_PERMISSION_KEY = 'crm:notif-permission-asked';
const SEEN_KEY = 'crm:hatirlatma-seen';

const musteriAd = (m: HatirlatmaMusteri | null): string => {
  if (!m) return '—';
  return m.tipi === 'KURUMSAL'
    ? (m.unvan ?? 'Kurum')
    : `${m.ad} ${m.soyad ?? ''}`.trim();
};

const dakikaSonra = (iso: string, simdi: Date): string => {
  const dk = Math.round(
    (new Date(iso).getTime() - simdi.getTime()) / 60000,
  );
  if (dk <= 0) return 'şimdi';
  if (dk < 60) return `${dk} dk sonra`;
  const saat = Math.floor(dk / 60);
  const kalanDk = dk % 60;
  return `${saat}s ${kalanDk}dk sonra`;
};

/**
 * HatirlatmaBanner — header'a yerleşir, yaklaşan hatırlatmaları gösterir.
 *
 * - 1 dakikada bir polling (TanStack Query refetchInterval)
 * - 0 hatırlatma → sadece gri zil ikonu
 * - 1+ hatırlatma → mavi rozet + sayı
 * - Tıklayınca dropdown panel
 * - Bell ikonuna ilk tıklandığında Notification API izni iste (KARAR-007/UX:
 *   agresif değil, kullanıcı kontrolünde)
 * - İzin verildiyse yeni hatırlatma çıkar çıkmaz browser notification gönder
 *   (`tag` ile duplikat engellenir, ayrıca localStorage'da gösterilen ID'ler
 *   tutulur)
 */
export function HatirlatmaBanner() {
  const [acik, setAcik] = useState(false);
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  );
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const seenRef = useRef<Set<string>>(loadSeen());

  const { data } = useQuery({
    queryKey: ['hatirlatmalar'],
    queryFn: () =>
      api
        .get('hatirlatmalar')
        .json<{ data: HatirlatmaResp }>()
        .then((r) => r.data),
    refetchInterval: 60_000, // 1 dakika
    staleTime: 30_000,
  });

  const aktiviteler = data?.aktiviteler ?? [];
  const olaylar = data?.olaylar ?? [];
  const toplam = aktiviteler.length + olaylar.length;
  const simdi = data?.simdi ? new Date(data.simdi) : new Date();

  // Dropdown dışına tıklayınca kapat
  useEffect(() => {
    if (!acik) return;
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setAcik(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [acik]);

  // Yeni hatırlatma çıkarsa browser notification gönder (izinliyse)
  useEffect(() => {
    if (notifPermission !== 'granted' || typeof Notification === 'undefined') {
      return;
    }
    const yeniler: { id: string; baslik: string; alt: string }[] = [];
    for (const a of aktiviteler) {
      if (!seenRef.current.has(`a-${a.id}`)) {
        yeniler.push({
          id: `a-${a.id}`,
          baslik: `Hatırlatma: ${a.baslik}`,
          alt: `${musteriAd(a.musteri)} · ${dakikaSonra(a.hatirlatma, simdi)}`,
        });
      }
    }
    for (const o of olaylar) {
      if (!seenRef.current.has(`o-${o.id}`)) {
        yeniler.push({
          id: `o-${o.id}`,
          baslik: `Hatırlatma: ${o.baslik}`,
          alt: `${musteriAd(o.musteri)} · ${dakikaSonra(o.hatirlatmaZamani, simdi)}`,
        });
      }
    }
    for (const y of yeniler) {
      try {
        new Notification(y.baslik, { body: y.alt, tag: y.id });
        seenRef.current.add(y.id);
      } catch {
        // Notification constructor bazı browser'larda izinsiz throw atar
      }
    }
    if (yeniler.length) {
      saveSeen(seenRef.current);
    }
  }, [aktiviteler, olaylar, notifPermission, simdi]);

  const handleBellClick = async () => {
    setAcik(!acik);
    // İlk açılışta izin sor (sessizce, kullanıcı zaten reddetmişse tekrar sorma)
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default' &&
      !localStorage.getItem(NOTIF_PERMISSION_KEY)
    ) {
      localStorage.setItem(NOTIF_PERMISSION_KEY, '1');
      const perm = await Notification.requestPermission();
      setNotifPermission(perm);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleBellClick}
        className={cn(
          'relative inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors',
          toplam > 0
            ? 'text-primary hover:bg-primary/10'
            : 'text-muted-foreground hover:bg-muted',
        )}
        aria-label="Hatırlatmalar"
      >
        <Bell className="h-4 w-4" />
        {toplam > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
            {toplam > 9 ? '9+' : toplam}
          </span>
        )}
      </button>

      {acik && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-md border bg-background shadow-lg">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h3 className="text-sm font-semibold">Hatırlatmalar</h3>
            <button
              type="button"
              onClick={() => setAcik(false)}
              className="rounded p-1 text-muted-foreground hover:bg-muted"
              aria-label="Kapat"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {notifPermission === 'denied' && (
            <div className="border-b bg-amber-50 px-4 py-2 text-xs text-amber-900">
              <BellOff className="mr-1 inline h-3 w-3" />
              Tarayıcı bildirimleri engellenmiş — sadece bu panelde
              görüntülenecek.
            </div>
          )}

          {toplam === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              <Bell className="mx-auto mb-2 h-6 w-6 opacity-40" />
              Yaklaşan hatırlatma yok
            </div>
          ) : (
            <ul className="max-h-96 divide-y overflow-y-auto">
              {aktiviteler.map((a) => (
                <li key={`a-${a.id}`}>
                  <Link
                    to={`/musteriler/${a.musteriId}`}
                    onClick={() => setAcik(false)}
                    className="flex gap-3 px-4 py-3 hover:bg-muted/50"
                  >
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                      <Phone className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {a.baslik}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {musteriAd(a.musteri)}
                      </p>
                      <p className="mt-0.5 text-xs text-primary">
                        {dakikaSonra(a.hatirlatma, simdi)}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
              {olaylar.map((o) => (
                <li key={`o-${o.id}`}>
                  <Link
                    to={o.musteriId ? `/musteriler/${o.musteriId}` : '/takvim'}
                    onClick={() => setAcik(false)}
                    className="flex gap-3 px-4 py-3 hover:bg-muted/50"
                  >
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                      <Calendar className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {o.baslik}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {musteriAd(o.musteri)}
                      </p>
                      <p className="mt-0.5 text-xs text-primary">
                        {dakikaSonra(o.hatirlatmaZamani, simdi)}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// localStorage helpers — gösterilmiş notification ID'leri
// ──────────────────────────────────────────────────────────

function loadSeen(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as string[];
    return new Set(parsed);
  } catch {
    return new Set();
  }
}

function saveSeen(set: Set<string>) {
  if (typeof window === 'undefined') return;
  // Limit 500 entry — eski kayıtları FIFO temizle
  const arr = Array.from(set).slice(-500);
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch {
    // localStorage dolu olabilir
  }
}
